import Phaser from "phaser";
import Arrow from "../Arrow";
import FloatingTexts from "../GUI/FloatText";
import ArcherCharacter from "../Character";
import Monster from "../Monster";
import Level from "../Story/Level";
import { Bows } from "../Bow";
import MobCursorMarker from "./MobCursorMarker";
import PositionMath from "../Math/PositionMath";
import StoryStorage from "../Storage/StoryStorage";
import PlayerInventoryStorage from "../Storage/PlayerInventoryStorage";
import ExperiencePoints from "../Character/ExperiencePoints";
import PauseButton from "../GUI/PauseButton";
import SkillButtonGUI from "../GUI/SkillButtonGUI";
import SkillRegistry from "../Skills/SkillRegistry";
import SkillManager from "../Skills/SkillManager";
import SkillStorage from "../Storage/SkillStorage";
import CharacterStorage from "../Storage/CharacterStorage";
import { isMobileDevice } from "../Utils/device";
import { ILevelInputController } from "../Input/ILevelInputController";
import DesktopLevelInputController from "../Input/DesktopLevelInputController";
import MobileLevelInputController from "../Input/MobileLevelInputController";

/**
 * Level scene is the scene that actually play the level.
 */
export default class LevelScene extends Phaser.Scene {
  private mBackground: Phaser.GameObjects.Image;

  private mStoryStorage: StoryStorage;
  private mInventoryStorage: PlayerInventoryStorage;

  private mCharacter: ArcherCharacter;
  private mMonsters: Monster[];
  private mLevel: Level;
  private mExperiencePoints: ExperiencePoints;
  private mIsFinishedLevel: boolean = false;

  private mMobCloseCursor: MobCursorMarker;

  private mGold: number = 0;
  private mExp: number = 0;
  private mMoneyIcon: Phaser.GameObjects.Image;
  private mMoneyText: Phaser.GameObjects.Text;
  private mLevelIcon: Phaser.GameObjects.Image;
  private mLevelText: Phaser.GameObjects.Text;
  private mPauseButton: PauseButton;

  private mTimeSeconds: number;
  private mTimer: Phaser.Time.TimerEvent;
  private mSecondsElapsed = 0;
  private mTimeText: Phaser.GameObjects.Text;
  private mFloatingTextx: FloatingTexts;

  private mGoldEarnedInLevel: number = 0;
  private mExpEarnedInLevel: number = 0;
  private mSkillButtons: SkillButtonGUI[] = [];
  private mInputController: ILevelInputController;

  constructor() {
    super({ key: "LevelScene" });
    this.mStoryStorage = new StoryStorage();
    this.mInventoryStorage = new PlayerInventoryStorage();
    this.mMobCloseCursor = new MobCursorMarker();

    const skillStorage = new SkillStorage();
    const charStorage = new CharacterStorage();
    const skillManager = new SkillManager(skillStorage);

    this.mCharacter = new ArcherCharacter(
      this,
      skillManager,
      charStorage,
      this.mStoryStorage,
      this.OnHitTarget,
      this.OnPlayerDamage,
      this.OnPlayerLose,
    );
    this.mFloatingTextx = new FloatingTexts(this);
  }

  init(level: Level) {
    this.mLevel = level;
    this.mTimeSeconds = level.DurationLimitMinutes * 60;
    this.mMonsters = [];
    this.mGold = this.mStoryStorage.GetMoney();
    this.mExp = this.mStoryStorage.GetExp();

    let currentBowName = this.mInventoryStorage.GetCurrentBow();
    let bow = Bows.GetBow(currentBowName);

    if (bow != undefined) {
      this.mCharacter.SetBow(bow);
      console.log(`Setting bow ${bow.Name}`);
    }
  }

  preload() {
    this.mCharacter.PreLoad();

    // Single-row HUD: [LevelIcon] [Level] [GoldIcon] [Gold] Time: [Time]
    // Icons centred at y=16; text top at y=8 so 15px Arial sits on the same baseline.
    const HUD_ICON_Y  = 24;
    const HUD_TEXT_Y  = 16;
    const LEVEL_ICON_X = 28;
    const LEVEL_TEXT_X = 46;
    const GOLD_ICON_X  = 84;
    const GOLD_TEXT_X  = 102;
    const TIME_TEXT_X  = 187;

    this.mLevelText = this.add.text(LEVEL_TEXT_X, HUD_TEXT_Y, this.mExp.toString(), {
      font: "15px Arial",
      color: "#000000",
    });
    this.mMoneyText = this.add.text(GOLD_TEXT_X, HUD_TEXT_Y, this.mGold.toString(), {
      font: "15px Arial",
      color: "#000000",
    });
    this.mTimeText = this.add.text(TIME_TEXT_X, HUD_TEXT_Y, `Time: ${this.mTimeSeconds}`, {
      font: "15px Arial",
      color: "#000000",
    });
    this.load.image("moneyIcon", "images/goldIcon.png");
    this.load.image("levelIcon", "images/levelIcon.png");

    this.load.image("redMark", "images/redMark.png");

    this.load.image("resumeButton", "images/resumeButton.png");
    this.load.image("exitButton", "images/exitButton.png");

    let scene = this;

    this.mLevel.Generators.forEach((generator) => {
      generator.PreLoad(scene);
    });

    PauseButton.preload(this);
    SkillRegistry.PreloadAllIcons(this);
  }

  create() {
    this.mTimer = this.time.addEvent({
      delay: 1000, // ms
      callback: this.OnTimer,
      //args: [],
      callbackScope: this,
      loop: true,
    });

    let width = this.cameras.main.width;
    let height = this.cameras.main.height;
    this.mBackground = this.add.image(
      width / 2,
      height / 2,
      this.mLevel.Background,
    );
    this.mBackground.setDisplaySize(width, height);
    this.mBackground.setDepth(-1);

    this.mLevelIcon = this.add.image(28, 24, "levelIcon");
    this.mMoneyIcon = this.add.image(84, 24, "moneyIcon");
    let scene = this;

    this.mLevel.Generators.forEach((generator) => {
      generator.Create(scene);
    });

    this.mExperiencePoints = ExperiencePoints.create();
    this.mLevelText.text = this.mExperiencePoints.Level.toString();

    let character = this.mCharacter;

    this.mCharacter.Create(this.mExperiencePoints);

    const mobile = isMobileDevice(this);
    const skillManager = this.mCharacter.GetSkillManager();

    this.mInputController = mobile
      ? new MobileLevelInputController(this, character)
      : new DesktopLevelInputController(this, character, skillManager);

    let endX = this.mCharacter.GetSprite().x;
    this.mMobCloseCursor.create(this, endX);

    if (this.input == null || this.input.keyboard == null) {
      return;
    }

    this.input.keyboard.on("keydown", (event) => {
      if (event.code == "Esc") {
        this.PauseGame();
      }
    });

    this.mPauseButton = new PauseButton(this);

    const activeStates = skillManager.GetActiveSkillStates();
    const keyMap: Record<string, string> = {
      ONE: "1",
      TWO: "2",
      THREE: "3",
      FOUR: "4",
    };

    const MOBILE_BTN_SIZE = 72;
    const MOBILE_BTN_SPACING = 90;

    let buttonX = mobile
      ? 60
      : this.scale.width / 2 - (activeStates.length * 70) / 2 + 35;
    // Mobile: skill 1 at top, going downward. Start position is the bottom-edge of skill 1.
    let buttonY = mobile
      ? this.scale.height - 580
      : this.scale.height - 10;

    for (const state of activeStates) {
      const label = keyMap[state.skill.KeyBinding] || "?";
      const btn = new SkillButtonGUI(
        this,
        state.skill,
        buttonX,
        buttonY,
        label,
        () => {
          skillManager.TryActivateSkill(
            state.skill.KeyBinding,
            this.mCharacter.GetArrowCaster(),
            this.mCharacter
          );
        },
        mobile ? MOBILE_BTN_SIZE : 48
      );
      this.mSkillButtons.push(btn);
      if (mobile) {
        buttonY += MOBILE_BTN_SPACING; // stack downward, skill 1 at top
      } else {
        buttonX += 70;
      }
    }
  }

  onShutdown() {
    this.mInputController?.Destroy();
    this.mCharacter.Destory();
    this.mPauseButton.destroy();
    this.mSkillButtons.forEach((btn) => btn.destroy());
    this.mSkillButtons = [];
  }

  onDestroy() {}

  update(time: number, delta: number) {
    if (this.mIsFinishedLevel) {
      console.log("Finished level yay");
      return;
    }

    this.mCharacter.Update(this.mMonsters);

    // Update skill cooldowns and button visuals
    const skillManager = this.mCharacter.GetSkillManager();
    skillManager.UpdateCooldowns(delta);
    const activeStates = skillManager.GetActiveSkillStates();
    for (
      let i = 0;
      i < this.mSkillButtons.length && i < activeStates.length;
      i++
    ) {
      this.mSkillButtons[i].UpdateCooldown(
        activeStates[i].cooldownRemaining,
        activeStates[i].skill.CooldownMs(activeStates[i].level)
      );
    }

    let thisScene = this;

    if (
      thisScene === null ||
      thisScene.input === null ||
      thisScene.input.keyboard === null
    ) {
      console.log("Error, everything is null in level scene");
      return;
    }

    this.mMonsters.forEach((m) => m.Update(1));

    // Timer or until monsters are dead.
    if (
      (this.mTimeSeconds <= 0 && this.mLevel.StopLevelWhenTimeout) ||
      (this.mTimeSeconds <= 0 &&
        !this.mLevel.StopLevelWhenTimeout &&
        this.mMonsters.length == 0)
    ) {
      console.log("Won level");
      thisScene.OnPlayerWin(thisScene);
    }

    this.mMobCloseCursor.update(this.mMonsters);

    const esc = thisScene.input.keyboard.addKey(
      Phaser.Input.Keyboard.KeyCodes.ESC,
    );
    this.events.on("update", () => {
      if (Phaser.Input.Keyboard.JustDown(esc)) {
        thisScene.PauseGame();
      }
    });
  }

  private OnHitTarget(_arrow: Arrow, monster: Monster, scene: Phaser.Scene) {
    const levelScene = scene as LevelScene;
    monster.Damage(_arrow.Damage);
    if (_arrow.IsCritical) {
      levelScene.mFloatingTextx.AddText(
        _arrow.Damage.toString(),
        monster.Sprite().body.x,
        monster.Sprite().body.y,
        "#ff36d4ff",
        "40px",
      );
    } else {
      levelScene.mFloatingTextx.AddText(
        _arrow.Damage.toString(),
        monster.Sprite().body.x,
        monster.Sprite().body.y,
      );
    }

    // Explosion AoE damage
    if (_arrow.IsExploding && _arrow.ExplosionRadius > 0) {
      const hitX = monster.Sprite().body.x;
      const hitY = monster.Sprite().body.y;

      levelScene.mMonsters.forEach((nearbyMonster) => {
        if (nearbyMonster === monster) return;
        const dx = nearbyMonster.Sprite().body.x - hitX;
        const dy = nearbyMonster.Sprite().body.y - hitY;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist <= _arrow.ExplosionRadius) {
          const splashDamage = Math.trunc(
            _arrow.Damage * _arrow.ExplosionDamageMultiplier
          );
          nearbyMonster.Damage(splashDamage);
          levelScene.mFloatingTextx.AddText(
            splashDamage.toString(),
            nearbyMonster.Sprite().body.x,
            nearbyMonster.Sprite().body.y,
            "#ff8800",
          );
        }
      });

      // Visual explosion effect
      const circle = levelScene.add.circle(
        hitX,
        hitY,
        _arrow.ExplosionRadius,
        0xff6600,
        0.3
      );
      levelScene.tweens.add({
        targets: circle,
        alpha: 0,
        scale: 1.5,
        duration: 300,
        onComplete: () => circle.destroy(),
      });

      // Process deaths from splash damage
      const deadMonsters = levelScene.mMonsters.filter(
        (m) => m !== monster && m.IsDead()
      );
      for (const dead of deadMonsters) {
        const money = dead.GetMoneyDropped();
        const expDropped = dead.GetExpDropped();
        levelScene.mGoldEarnedInLevel += money;
        levelScene.mExpEarnedInLevel += expDropped;

        let goldPos = PositionMath.calculateFloatTextXY(
          dead.Sprite().body.x,
          dead.Sprite().body.y,
        );
        levelScene.mFloatingTextx.AddText(
          money.toString(),
          goldPos.newX,
          goldPos.newY,
          "#FFD54A",
        );

        const idx = levelScene.mMonsters.indexOf(dead);
        if (idx > -1) {
          dead.Destroy();
          levelScene.mMonsters.splice(idx, 1);
        }
      }
    }

    if (!monster.IsDead()) {
      return;
    }

    let money = monster.GetMoneyDropped();
    let expDropped = monster.GetExpDropped();
    levelScene.mGoldEarnedInLevel += money;
    levelScene.mExpEarnedInLevel += expDropped;

    let goldPos = PositionMath.calculateFloatTextXY(
      monster.Sprite().body.x,
      monster.Sprite().body.y,
    );
    levelScene.mFloatingTextx.AddText(
      money.toString(),
      goldPos.newX,
      goldPos.newY,
      "#FFD54A",
    );
    let expPos = PositionMath.calculateFloatTextXY(
      monster.Sprite().body.x,
      monster.Sprite().body.y,
    );
    levelScene.mFloatingTextx.AddText(
      expDropped.toString(),
      expPos.newX,
      expPos.newY,
      "#8A6CFF",
    );

    const index = levelScene.mMonsters.indexOf(monster, 0);
    if (index > -1) {
      monster.Destroy();
      levelScene.mMonsters.splice(index, 1);
    }
  }

  private OnPlayerDamage(
    scene,
    monster: Monster,
    finalDamageByMonster: number,
  ) {
    scene.mFloatingTextx.AddText(
      finalDamageByMonster,
      monster.Sprite().body.x + 20,
      monster.Sprite().body.y + 50,
      "#8d0c00ff",
    );

    const index = scene.mMonsters.indexOf(monster, 0);
    if (index > -1) {
      monster.Destroy();
      scene.mMonsters.splice(index, 1);
    }

    monster.Destroy();
  }

  private OnTimer() {
    let scene = this;

    this.mCharacter.RegenMana();

    this.mTimeSeconds--;
    if (this.mTimeSeconds <= 0) {
      this.mTimeSeconds = 0;
    }
    this.mSecondsElapsed++;

    var elapsed = this.mSecondsElapsed;

    let size = this.sys.game.scale.gameSize;

    let generatorFinish = false;
    this.mLevel.Generators.forEach((generator) => {
      if (generator.IsActive) {
        generator.IsActive = elapsed < generator.DurationActiveMinutes * 60;
      } else {
        generatorFinish = generatorFinish && true;
        return;
      }

      if (generator.ShouldGenerate(elapsed)) {
        let monsters = generator.Generate();

        monsters.forEach((monster) => {
          //Do i need to adjust? Test that out
          // let yAxisAdjust = (monster.Sprite().height / 2);
          //monster.SetPosition(new Phaser.Math.Vector2(size.width, size.height - yAxisAdjust));
          scene.mMonsters.push(monster);
        });
      }
    });

    this.mTimeText.setText(`Time: ${this.mTimeSeconds}`);
  }

  private OnPlayerWin(scene: LevelScene) {
    scene.mCharacter.Destory();
    scene.scene.stop("LevelScene");
    scene.scene.remove("LevelScene");
    scene.scene.start("WinLevelScene", {
      EarnedGold: this.mGoldEarnedInLevel + this.mLevel.GoldEarn,
      EarnedExp: this.mExpEarnedInLevel + this.mLevel.ExpEarn,
      LevelName: this.mLevel.Name,
      MobMarkerPercent: this.mMobCloseCursor.GetPositionPercentFromEnd(),
      ExperiencePoints: this.mExperiencePoints,
      CharacterAttributes: this.mCharacter.GetAttributes(),
    });
  }

  private OnPlayerLose(scene: Phaser.Scene) {
    const levelScene = scene as LevelScene;
    const percentGoldLoss = 10;
    const lostGold = Math.floor(levelScene.mGold / percentGoldLoss);
    levelScene.mStoryStorage.RemoveMoney(lostGold);

    levelScene.time.delayedCall(205, () => {
      levelScene.mCharacter.Destory();
      levelScene.scene.stop("LevelScene");
      levelScene.scene.remove("LevelScene");
      levelScene.scene.start("FailLevelScene", { LostGold: lostGold });
    });
  }

  private PauseGame() {
    this.mPauseButton.pause();
  }
}
